import raf from 'raf';
import { isArrayWithLength, isNonTabletMobileDevice } from '../../../helpers';

/****** PRIVATE METHODS *******/
const isArrayOfObjects = (value) => {
  return value && value[0] === Object(value[0]);
};

const easeOutCubic = (t, b, c, d) => (c * ((t = t / d - 1) * t * t + 1) + b);

const isDocumentElement = (element) => {
  return [document.documentElement, document.body, window].indexOf(element) > -1;
};

const getScrollTop = (element) => {
  return isDocumentElement(element) ? window.pageYOffset : element.scrollTop;
};

const scrollTo = (element, top) => {
  if (isDocumentElement(element)) {
    window.scrollTo(0, top);
    return;
  }
  element.scrollTop = top;
};

const smoothScrollTo = (element, to, duration = 350, callback = undefined) => {
  const start = getScrollTop(element);
  const change = to - start;
  let currentTime = 0;

  const smoothScroller = () => {
    currentTime += 10;
    scrollTo(element, easeOutCubic(currentTime, start, change, duration));
    if (currentTime < duration) {
      raf(smoothScroller);
    } else {
      callback && callback(element);
    }
  };

  smoothScroller();
};

/****** EXPORTED METHODS *******/
export const scrollMenuIntoViewOnOpen = (menuEl) => {
  // Note a react node OR is a hand-held mobile device
  if (!menuEl || !menuEl.getBoundingClientRect || isNonTabletMobileDevice()) {
    return;
  }

  const scrollParent = document.documentElement;
  const scrollParentRect = scrollParent.getBoundingClientRect();
  const menuRect = menuEl.getBoundingClientRect();

  const viewHeight = window.innerHeight;
  const scrollTop = getScrollTop(scrollParent);
  const marginBottom = parseInt(getComputedStyle(menuEl).marginBottom, 10);
  const viewSpaceBelow = viewHeight - menuRect.top;

  // menu will fit in space (no need to scroll)
  if (viewSpaceBelow >= menuRect.height) {
    return;
  }

  const scrollSpaceBelow = scrollParentRect.height - scrollTop - menuRect.top;
  const scrollDown = menuRect.bottom - viewHeight + scrollTop + marginBottom;

  // If we can fit the entire menu into view - do smooth scroll
  if (scrollSpaceBelow >= menuRect.height) {
    smoothScrollTo(scrollParent, scrollDown);
  }
};

export const scrollToFocusedOption = (menuEl, focusedEl) => {
  const menuRect = menuEl.getBoundingClientRect();
  const focusedRect = focusedEl.getBoundingClientRect();
  const overScroll = focusedEl.offsetHeight / 3;

  if (focusedRect.bottom + overScroll > menuRect.bottom) {
    scrollTo(menuEl, Math.min(focusedEl.offsetTop + focusedEl.clientHeight - menuEl.offsetHeight + overScroll, menuEl.scrollHeight));
  } else if (focusedRect.top - overScroll < menuRect.top) {
    scrollTo(menuEl, Math.max(focusedEl.offsetTop - overScroll, 0));
  }
};

export const getSlicedArray = (arr, end, start = 0) => {
  return isArrayWithLength(arr) ? arr.slice(start, end) : [];
};

export const getPrevOptionsTrimStart = (prevOptions, serverOptionsSubset, maxItemsThreshold) => {
  const prevOptionsLen = isArrayWithLength(prevOptions) ? prevOptions.length : 0;
  const serverOptionsSubsetLen = isArrayWithLength(serverOptionsSubset) ? serverOptionsSubset.length : 0;
  const bufferSpace = maxItemsThreshold - (prevOptionsLen + serverOptionsSubsetLen);
  const prevOptionsStartIndex = bufferSpace < 0 ? Math.abs(bufferSpace) : 0;
  return isArrayWithLength(prevOptions) ? prevOptions.slice(prevOptionsStartIndex) : [];
};

export const getFocusOptionIndex = (option) => {
  return option ? option.index : -1;
};

export const trimAndFormatStr = (value) => {
  return value.replace(/^\s+|\s+$/g, '').toLowerCase();
};

export const isObject = (value) => {
  return value && (typeof value === 'object') && (value.constructor === Object);
};

export const normalizeSelectedValue = (value, options, getOptionValue) => {
  // Check if we need to Extract the selectedValue via the the mapped value - i.e. selectedOption.value is mapped to dropdown value
  let finalValue = null;
  if (value && !isObject(value) && isArrayOfObjects(options)) {
    finalValue = options.find((option) => value === getOptionValue(option));
  }

  if (!finalValue) {
    finalValue = value;
  }

  if (Array.isArray(finalValue)) return finalValue[0];
  if (isObject(finalValue)) return finalValue;
  return null;
};